import { useMemo, useState } from "react";
import { useLanguage } from "../../i18n/LanguageProvider";
import { useCurrency } from "../../currency/CurrencyProvider";
import { areaColor } from "../home/constants";
import { DailyAreaDay, DailyThreadData } from "./forumApi";

const hh = (h: number) => String(h).padStart(2, "0");
const windowLabel = (c: { start: number } | null | undefined) =>
  c ? `${hh(c.start)}–${hh((c.start + 3) % 24)}` : "–";

const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

// "Måndag 26 oktober" / "Mån 26 okt": capitalised first letter only, no trailing dot.
export const dayLabel = (date: string, locale: string, weekday: "long" | "short" = "long", month: "long" | "short" = "long") =>
  cap(
    new Date(`${date}T12:00:00Z`)
      .toLocaleDateString(locale, { timeZone: "UTC", weekday, day: "numeric", month })
      .replace(/\.$/, ""),
  );

// Hourly bars across several days; the cheapest 3-hour window of each day is
// highlighted and days are separated by a gap and a label.
const PeriodBars: React.FC<{
  days: { date: string; day?: DailyAreaDay }[];
  color: string;
  selected: number;
  onSelect: (i: number) => void;
  locale: string;
}> = ({ days, color, selected, onSelect, locale }) => {
  const max = Math.max(0.001, ...days.flatMap((d) => d.day?.hourly.map((v) => v ?? 0) ?? []));
  const BAR = 5;
  const GAP = 10;
  const H = 90;
  const W = days.length * 24 * BAR + (days.length - 1) * GAP;
  return (
    <svg className="fm-period" viewBox={`0 0 ${W} ${H + 16}`} role="img">
      {days.map((d, i) => {
        const x0 = i * (24 * BAR + GAP);
        const c = d.day?.cheapest3h;
        return (
          <g key={d.date} onClick={() => onSelect(i)} className={i === selected ? "is-selected" : ""}>
            <rect x={x0 - 2} y={0} width={24 * BAR + 4} height={H + 16} rx={6} className="fm-period__hit" />
            {d.day?.hourly.map((v, h) => {
              if (v === null) return null;
              const bh = Math.max((Math.max(v, 0) / max) * (H - 6), 1.5);
              const cheap = c && h >= c.start && h < c.start + 3;
              return (
                <rect
                  key={h}
                  x={x0 + h * BAR + 0.5}
                  y={H - bh}
                  width={BAR - 1}
                  height={bh}
                  rx={1}
                  fill={cheap ? "#10b981" : color}
                  opacity={cheap ? 1 : i === selected ? 0.85 : 0.4}
                />
              );
            })}
            <text x={x0 + 12 * BAR} y={H + 13} textAnchor="middle" className="fm-period__label">
              {dayLabel(d.date, locale, "short", "short").replace(/\.$/, "")}
            </text>
          </g>
        );
      })}
    </svg>
  );
};

export const DailyCard: React.FC<{ data: DailyThreadData }> = ({ data }) => {
  const { t, locale } = useLanguage();
  const { format, unit } = useCurrency();
  const dark = document.documentElement.getAttribute("data-bs-theme") === "dark";
  const [zone, setZone] = useState(data.areas.includes("SE3") ? "SE3" : data.areas[0]);
  const [sel, setSel] = useState(0);
  const outlook = data.outlook ?? [];
  const hasDetail = outlook.some((o) => o.day);
  const cheapestDay = useMemo(() => {
    let best = -1;
    outlook.forEach((o, i) => {
      const v = o.values[zone];
      if (v !== undefined && (best < 0 || v < outlook[best].values[zone])) best = i;
    });
    return best;
  }, [outlook, zone]);
  const selDay = outlook[sel]?.day?.[zone];
  const tomorrow = dayLabel(data.date, locale);

  return (
    <div className="fm-daily">
      {outlook.length > 0 && (
        <>
          <div className="fm-daily__head">
            <span>
              {t("forum.daily.outlookTitle", {
                from: dayLabel(outlook[0].date, locale, "short", "short"),
                to: dayLabel(outlook[outlook.length - 1].date, locale, "short", "short"),
              })}
            </span>
            <span className="fm-daily__unit">{unit}</span>
          </div>
          <div className="fm-zone-pills" role="tablist">
            {data.areas.map((a) => (
              <button
                key={a}
                type="button"
                role="tab"
                aria-selected={a === zone}
                className={a === zone ? "is-active" : ""}
                style={{ "--zone": areaColor(a, dark) } as React.CSSProperties}
                onClick={() => setZone(a)}
              >
                <span className="fm-dot" style={{ background: areaColor(a, dark) }} />
                {a}
              </button>
            ))}
          </div>

          <div className="fm-days">
            {outlook.map((o, i) => {
              const d = o.day?.[zone];
              const v = o.values[zone];
              return (
                <button
                  key={o.date}
                  type="button"
                  className={`fm-day ${i === sel ? "is-active" : ""} ${i === cheapestDay ? "is-cheapest" : ""}`}
                  onClick={() => setSel(i)}
                >
                  <span className="fm-day__name">{dayLabel(o.date, locale, "short", "short").replace(/\.$/, "")}</span>
                  <span className="fm-day__value">{v !== undefined ? format(v) : "–"}</span>
                  {d && (
                    <span className="fm-day__range">
                      {format(d.min)}–{format(d.max)}
                    </span>
                  )}
                  {i === cheapestDay && <span className="fm-day__tag">{t("forum.daily.cheapestDay")}</span>}
                </button>
              );
            })}
          </div>

          {hasDetail && (
            <>
              <PeriodBars
                days={outlook.map((o) => ({ date: o.date, day: o.day?.[zone] }))}
                color={areaColor(zone, dark)}
                selected={sel}
                onSelect={setSel}
                locale={locale}
              />
              {selDay && (
                <div className="fm-day-facts">
                  <span>
                    <b>{dayLabel(outlook[sel].date, locale)}</b> · {zone}
                  </span>
                  <span>
                    {t("forum.daily.avg")} <b>{format(selDay.avg)}</b>
                  </span>
                  <span>
                    {t("forum.daily.min")} <b>{format(selDay.min)}</b> {hh(selDay.minHour)}
                  </span>
                  <span>
                    {t("forum.daily.max")} <b>{format(selDay.max)}</b> {hh(selDay.maxHour)}
                  </span>
                  <span className="is-green">
                    {t("forum.daily.cheapest")} <b>{windowLabel(selDay.cheapest3h)}</b>
                  </span>
                </div>
              )}
            </>
          )}

          <div className="fm-table-wrap">
            <table className="fm-daily__table">
              <thead>
                <tr>
                  <th />
                  {data.areas.map((a) => (
                    <th key={a}>{a}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {outlook.map((o, i) => (
                  <tr key={o.date} className={i === sel ? "is-active" : ""} onClick={() => setSel(i)}>
                    <td>{dayLabel(o.date, locale, "short", "long")}</td>
                    {data.areas.map((a) => (
                      <td key={a} className={`num ${a === zone ? "strong" : ""}`}>
                        {o.values[a] !== undefined ? format(o.values[a]) : "–"}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      <div className="fm-known">
        <div className="fm-known__head">
          <span>{t(data.pricesFinal ? "forum.daily.knownTitle" : "forum.daily.forecast", { date: tomorrow })}</span>
          {data.pricesFinal && <span className="fm-known__badge">Nord Pool</span>}
        </div>
        <div className="fm-table-wrap">
          <table className="fm-daily__table">
            <thead>
              <tr>
                <th>{t("forum.daily.zone")}</th>
                <th>{t("forum.daily.avg")}</th>
                <th>{t("forum.daily.min")}</th>
                <th>{t("forum.daily.max")}</th>
                <th>{t("forum.daily.cheapest")}</th>
                {data.pricesFinal && <th>{t("forum.daily.ourForecast")}</th>}
              </tr>
            </thead>
            <tbody>
              {data.areas.map((a) => {
                const d = data.day[a];
                const miss = data.pricesFinal && d.forecastAvg !== null ? d.avg - d.forecastAvg : null;
                return (
                  <tr key={a} className={a === zone ? "is-active" : ""} onClick={() => setZone(a)}>
                    <td>
                      <span className="fm-dot" style={{ background: areaColor(a, dark) }} />
                      {a}
                    </td>
                    <td className="num strong">{format(d.avg)}</td>
                    <td className="num">
                      {format(d.min)} <span className="muted">{hh(d.minHour)}</span>
                    </td>
                    <td className="num">
                      {format(d.max)} <span className="muted">{hh(d.maxHour)}</span>
                    </td>
                    <td className="num">{windowLabel(d.cheapest3h)}</td>
                    {data.pricesFinal && (
                      <td className="num">
                        {d.forecastAvg !== null ? format(d.forecastAvg) : "–"}
                        {miss !== null && (
                          <span className={`fm-miss ${miss <= 0 ? "is-down" : "is-up"}`}>
                            {miss > 0 ? "+" : ""}
                            {format(miss)}
                          </span>
                        )}
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
